import apiClient from "./apiClient";
import { userFcmTokenEndPoint } from "./endpoints";

interface FcmTokenPayload {
  token: string;
  provider: "firebase";
}

const MAX_SYNC_ATTEMPTS = 3;
const INITIAL_BACKOFF_MS = 400;

/**
 * Returns true when local auth data suggests the user is signed in.
 */
const hasLocalAuthenticatedUser = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const rawUserData = localStorage.getItem("userData");

    if (!rawUserData) {
      return false;
    }

    const parsedUserData = JSON.parse(rawUserData) as { id?: number; user_id?: number; username?: string };
    return Boolean(parsedUserData.id || parsedUserData.user_id || parsedUserData.username);
  } catch {
    return false;
  }
};

/**
 * Waits for a delay used between retry attempts.
 */
const wait = async (delayMs: number): Promise<void> => {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, delayMs);
  });
};

/**
 * Extracts HTTP status from an apiClient error message when possible.
 */
const getHttpStatusFromError = (error: unknown): number | null => {
  if (!(error instanceof Error)) {
    return null;
  }

  const statusMatch = error.message.match(/HTTP\s+(\d{3})/i);
  return statusMatch ? Number(statusMatch[1]) : null;
};

/**
 * Determines whether sending the token should be retried.
 */
const shouldRetrySync = (error: unknown, attempt: number): boolean => {
  if (attempt >= MAX_SYNC_ATTEMPTS) {
    return false;
  }

  const status = getHttpStatusFromError(error);

  if (status === null) {
    return true;
  }

  if (status >= 500) {
    return true;
  }

  return false;
};

/**
 * Sends the Firebase Cloud Messaging token to the backend for the current user.
 */
export const syncFcmTokenToBackend = async (token: string): Promise<boolean> => {
  if (!token || token.trim().length === 0) {
    return false;
  }

  // Avoid triggering auth logout flows when no authenticated user is present yet.
  if (!hasLocalAuthenticatedUser()) {
    return false;
  }

  const payload: FcmTokenPayload = {
    token,
    provider: "firebase",
  };

  let attempt = 1;

  while (attempt <= MAX_SYNC_ATTEMPTS) {
    try {
      await apiClient.post(userFcmTokenEndPoint, payload);
      return true;
    } catch (error) {
      if (!shouldRetrySync(error, attempt)) {
        throw error;
      }

      const backoffDelay = INITIAL_BACKOFF_MS * 2 ** (attempt - 1);
      await wait(backoffDelay);
      attempt += 1;
    }
  }

  return false;
};
